import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { Briefcase, Loader2, SearchX, Send, UsersRound, X, XCircle } from "lucide-react";
import { money, seesMoney, type ContractorRow } from "@contractorhub/shared";
import { apiFetch, errorMessage } from "../../../lib/api";
import { adminClientsQuery, adminContractorsQuery } from "../../../lib/queries";
import {
  ActionMenu,
  Alert,
  Avatar,
  Button,
  Card,
  DataTable,
  EmptyState,
  Modal,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
  StatusBadge,
  actionMenuItemClass,
  type Column,
} from "../../../components/ui";
import { EditContractorDialog } from "../../../components/edit-dialogs";
import { InviteLinkPanel } from "../../../components/invite-link-panel";
import { ManageAssignmentsDialog } from "../../../components/manage-assignments-dialog";
import { NewContractorDialog } from "../../../components/new-contractor-dialog";

/*
 * A user admin shares this page with full admins (AUTH-13), minus the money: the
 * API sends them no rates, budgets or spend, so the page shows no rate column
 * and a project made from here asks for no budget.
 */
export const Route = createFileRoute("/_authed/_staff/contractors")({
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(adminContractorsQuery),
      context.queryClient.ensureQueryData(adminClientsQuery),
    ]),
  component: ContractorsPage,
});

const PAGE_SIZE = 25;

/** A freshly re-sent invite link, held only until the admin dismisses it. */
type ReissuedInvite = {
  name: string;
  email: string;
  inviteLink: string;
  emailed: boolean;
  provisioned: boolean;
};

function statusOf(contractor: ContractorRow): string {
  if (contractor.status === "INVITED") {
    return contractor.revokedInviteId ? "Revoked" : "Invited";
  }
  return contractor.status === "ACTIVE" ? "Active" : "Inactive";
}

/** The four states the badge column can show — the filter offers exactly these. */
const STATUS_FILTERS = ["Active", "Invited", "Revoked", "Inactive"] as const;

/**
 * The project filter's answer for "nobody is paying for this person right now".
 *
 * A sentinel rather than an id, because "no project" is not a project. Bench
 * time is the question the Projects column exists to raise and had no way to
 * ask: with eleven contractors you can eyeball the dashes, with sixty you
 * cannot.
 */
const UNASSIGNED = "__unassigned__";

function ContractorsPage() {
  const { user } = Route.useRouteContext();
  const fullAdmin = seesMoney(user.role);
  const { data } = useSuspenseQuery(adminContractorsQuery);
  const { data: clientData } = useQuery(adminClientsQuery);
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<ReissuedInvite | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [projectId, setProjectId] = useState("");
  const [page, setPage] = useState(1);

  const active = data.contractors.filter((c) => c.status === "ACTIVE").length;
  const filtersOn = Boolean(search || status || projectId);

  /** Any filter change invalidates the page number — page 4 of 1 is a blank table. */
  function applyFilter(set: () => void) {
    set();
    setPage(1);
  }

  function clearFilters() {
    setSearch("");
    setStatus("");
    setProjectId("");
    setPage(1);
  }

  /*
   * Filtering and paging happen here, on the client.
   *
   * `GET /api/manage/contractors` returns every contractor in one unpaginated
   * response — a known gap recorded in CLAUDE.md — so this page used to render
   * the entire list into one `<tbody>`. Slicing it here bounds what the browser
   * has to lay out and gives the admin a way to find one person, but the
   * *response* is still unbounded. The real fix is server-side paging on that
   * endpoint, which is an API change and deliberately out of scope here.
   */
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return data.contractors.filter((c) => {
      if (
        needle &&
        ![c.name, c.email, c.companyName ?? ""].some((field) =>
          field.toLowerCase().includes(needle)
        )
      ) {
        return false;
      }
      if (status && statusOf(c) !== status) return false;
      if (projectId) {
        // Only *active* assignments count, matching what the Projects column
        // shows. Filtering on an ended engagement would return people the row
        // itself renders as unassigned.
        const live = c.assignments.filter((a) => a.active);
        if (projectId === UNASSIGNED) return live.length === 0;
        if (!live.some((a) => a.projectId === projectId)) return false;
      }
      return true;
    });
  }, [data.contractors, search, status, projectId]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const visible = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const allColumns: Column<ContractorRow>[] = [
    {
      key: "name",
      header: "Name",
      cell: (contractor) => (
        <div className="flex items-center gap-3">
          <Avatar name={contractor.name} />
          <div className="min-w-0">
            <div className="truncate font-medium text-text">{contractor.name}</div>
            {/* Email has its own column from `md` up; below that it folds in
                here rather than vanishing. */}
            <div className="truncate text-xs text-subtle md:hidden">
              {contractor.email}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: "email",
      header: "Email",
      priority: "md",
      cell: (contractor) => <span className="text-muted">{contractor.email}</span>,
    },
    {
      key: "company",
      header: "Company",
      priority: "lg",
      cell: (contractor) => (
        <span className="text-muted">{contractor.companyName ?? "—"}</span>
      ),
    },
    {
      key: "projects",
      header: "Projects",
      priority: "lg",
      cell: (contractor) => {
        const assignments = contractor.assignments.filter((a) => a.active);
        if (assignments.length === 0) return <span className="text-subtle">—</span>;
        return (
          <div className="space-y-0.5">
            {assignments.map((a) => (
              <div key={a.projectId} className="whitespace-nowrap text-muted">
                {a.clientName} · {a.projectName}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      key: "rate",
      header: "Daily rate",
      align: "right",
      priority: "sm",
      cell: (contractor) => {
        const assignments = contractor.assignments.filter((a) => a.active);
        if (assignments.length === 0) return <span className="text-subtle">—</span>;
        return (
          <div className="space-y-0.5">
            {assignments.map((a) => (
              // A missing rate is *missing*, not zero — saying "$0" would be a
              // claim about pay that nobody has made.
              <div key={a.projectId} className="text-muted">
                {a.currentRate && a.dailyRate !== undefined ? (
                  money(a.dailyRate)
                ) : (
                  <span className="text-subtle">Rate not set</span>
                )}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      key: "status",
      header: "Status",
      cell: (contractor) => <StatusBadge status={statusOf(contractor)} />,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      // A component, not inline JSX: the row actions own three mutations, and
      // hooks cannot live inside a `cell` callback.
      cell: (contractor) => (
        <ContractorActions
          contractor={contractor}
          showRates={fullAdmin}
          onError={setError}
          onInviteReissued={setIssued}
        />
      ),
    },
  ];
  const columns = fullAdmin ? allColumns : allColumns.filter((c) => c.key !== "rate");

  return (
    <div>
      <PageHeader
        title="Contractors"
        description={`${data.contractors.length} contractors · ${active} active`}
        actions={
          <NewContractorDialog
            projects={data.projects}
            clients={clientData?.clients ?? []}
            contractors={data.contractors}
            showRate={fullAdmin}
          />
        }
      />

      {error && (
        <Alert tone="error" className="mb-4">
          {error}
        </Alert>
      )}

      <Card className="overflow-hidden">
        <div className="flex flex-col gap-2 border-b border-line p-3 lg:flex-row lg:items-center">
          <div className="lg:w-72">
            <SearchInput
              aria-label="Search contractors"
              placeholder="Search name, email or company"
              value={search}
              /* Nothing leaves the browser, so a debounce would only add
                 latency between the keystroke and the rows moving. */
              debounceMs={0}
              onChange={(next) => applyFilter(() => setSearch(next))}
            />
          </div>

          <div className="flex flex-1 flex-col gap-2 sm:flex-row">
            <Select
              aria-label="Filter by status"
              className="sm:w-40"
              value={status}
              onChange={(event) => applyFilter(() => setStatus(event.target.value))}
            >
              <option value="">All statuses</option>
              {STATUS_FILTERS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>

            <Select
              aria-label="Filter by project"
              className="sm:w-64"
              value={projectId}
              onChange={(event) => applyFilter(() => setProjectId(event.target.value))}
            >
              <option value="">All projects</option>
              <option value={UNASSIGNED}>Not on a project</option>
              {data.projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.clientName} · {project.name}
                </option>
              ))}
            </Select>
          </div>

          {filtersOn && (
            <Button variant="ghost" size="sm" icon={X} onClick={clearFilters}>
              Clear
            </Button>
          )}
        </div>

        <DataTable
          columns={columns}
          rows={visible}
          getRowKey={(contractor) => contractor.id}
          caption="Contractors"
          stickyHeader
          empty={
            filtersOn ? (
              <EmptyState
                icon={SearchX}
                title="No matches"
                // Naming the filters, not just the search box: with three
                // controls the one excluding everything is rarely the one the
                // eye lands on.
                description={
                  search
                    ? `Nothing matches “${search.trim()}” with the current filters.`
                    : "No contractor matches the current filters."
                }
                action={
                  <Button variant="secondary" icon={X} onClick={clearFilters}>
                    Clear filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={UsersRound}
                title="No contractors yet"
                description="Add your first contractor to start collecting timesheets."
              />
            )
          }
        />

        <Pagination
          page={safePage}
          pageSize={PAGE_SIZE}
          total={filtered.length}
          onPageChange={setPage}
        />
      </Card>

      {/* One modal for the whole table: only ever one re-sent link at a time. */}
      <Modal open={issued !== null} onClose={() => setIssued(null)} title="Invite re-sent">
        {issued && (
          <InviteLinkPanel
            name={issued.name}
            email={issued.email}
            inviteLink={issued.inviteLink}
            emailed={issued.emailed}
            provisioned={issued.provisioned}
            onDone={() => setIssued(null)}
          />
        )}
      </Modal>
    </div>
  );
}

function ContractorActions({
  contractor,
  showRates,
  onError,
  onInviteReissued,
}: {
  contractor: ContractorRow;
  showRates: boolean;
  onError: (message: string | null) => void;
  onInviteReissued: (issued: ReissuedInvite) => void;
}) {
  const queryClient = useQueryClient();
  const [managing, setManaging] = useState(false);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["admin"] });

  const setStatus = useMutation({
    mutationFn: (status: "ACTIVE" | "INACTIVE") =>
      apiFetch("POST /api/manage/contractors/{id}/status", {
        params: { id: contractor.id },
        body: { status },
      }),
    onSuccess: async () => {
      onError(null);
      await refresh();
    },
    onError: (err) => onError(errorMessage(err)),
  });

  const resend = useMutation({
    mutationFn: (inviteId: string) =>
      apiFetch("POST /api/manage/invites/{id}/resend", { params: { id: inviteId } }),
    onSuccess: async ({ inviteLink, emailed, provisioned }) => {
      onError(null);
      onInviteReissued({
        name: contractor.name,
        email: contractor.email,
        inviteLink,
        emailed,
        provisioned,
      });
      await refresh();
    },
    onError: (err) => onError(errorMessage(err)),
  });

  const revoke = useMutation({
    mutationFn: (inviteId: string) =>
      apiFetch("POST /api/manage/invites/{id}/revoke", { params: { id: inviteId } }),
    onSuccess: async () => {
      onError(null);
      await refresh();
    },
    onError: (err) => onError(errorMessage(err)),
  });

  const isActive = contractor.status === "ACTIVE";
  const isInvited = contractor.status === "INVITED";
  const busy = setStatus.isPending || resend.isPending || revoke.isPending;

  return (
    <div className="flex justify-end">
      <ActionMenu label={`Actions for ${contractor.name}`}>
        <EditContractorDialog
          contractor={contractor}
          buttonClassName={actionMenuItemClass}
        />
        <button
          className={actionMenuItemClass}
          disabled={busy}
          onClick={() => setManaging(true)}
          title={
            showRates
              ? "Assign to a project, change a rate, or end an engagement"
              : "Assign to a project or end an engagement"
          }
        >
          <Briefcase className="h-4 w-4" aria-hidden="true" />
          Assignments
        </button>

        {isInvited && contractor.pendingInviteId ? (
          <>
            <button
              className={actionMenuItemClass}
              disabled={busy}
              onClick={() => resend.mutate(contractor.pendingInviteId!)}
              title="Generate a fresh invite link"
            >
              {resend.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Send className="h-4 w-4" aria-hidden="true" />
              )}
              Resend invite
            </button>
            <button
              className={actionMenuItemClass}
              disabled={busy}
              onClick={() => revoke.mutate(contractor.pendingInviteId!)}
              title="Cancel this invite"
            >
              {revoke.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <XCircle className="h-4 w-4" aria-hidden="true" />
              )}
              Revoke
            </button>
          </>
        ) : isInvited && contractor.revokedInviteId ? (
          <button
            className={actionMenuItemClass}
            disabled={busy}
            onClick={() => resend.mutate(contractor.revokedInviteId!)}
          >
            {resend.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Send className="h-4 w-4" aria-hidden="true" />
            )}
            Re-invite
          </button>
        ) : (
          <button
            className={actionMenuItemClass}
            disabled={busy}
            onClick={() => setStatus.mutate(isActive ? "INACTIVE" : "ACTIVE")}
          >
            {setStatus.isPending && (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            )}
            {isActive ? "Deactivate" : "Reactivate"}
          </button>
        )}
      </ActionMenu>

      <ManageAssignmentsDialog
        contractor={contractor}
        open={managing}
        onClose={() => setManaging(false)}
        showRates={showRates}
      />
    </div>
  );
}
